import React, { useEffect, useState } from "react";
import axios from "axios";
import studentId from "../config";
import { useNavigate } from "react-router-dom";

const EditProfile = () => {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents/${studentId}`
        );
        setStudent(res.data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    fetchStudent();
  }, []);

  if (loading) return <div className="text-center py-6">Loading...</div>;
  if (error) return <div className="text-center py-6 text-red-500">{error}</div>;

  const handleChange = (e) => {
    setStudent({ ...student, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let imageUrl = student.image;

      if (imageFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", imageFile);
        formData.append("fileName", imageFile.name);

        const imgRes = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/upload-to-imagekit`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        imageUrl = imgRes.data.url;
        setUploading(false);
      }

      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/students/updateStudent/${studentId}`,
        { ...student, image: imageUrl }
      );

      alert("Profile updated successfully!");
      navigate("/view-profile");
    } catch (err) {
      console.error(err);
      alert("Error updating profile");
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto bg-white p-8 rounded-xl shadow-md">
        <h1 className="text-3xl font-semibold text-center mb-6 text-gray-800">
          Edit Profile
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Image */}
          <div className="flex flex-col items-center">
            <div className="w-40 h-40 mb-2 rounded-full overflow-hidden shadow-md border border-gray-300">
              <img
                src={
                  imageFile
                    ? URL.createObjectURL(imageFile)
                    : student.image || "/default-avatar.png"
                }
                alt="Profile"
                className="w-full h-full object-cover"
              />
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="text-sm text-gray-600"
            />
          </div>

          {/* Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              label="First Name"
              name="firstName"
              value={student.firstName}
              onChange={handleChange}
            />
            <InputField
              label="Last Name"
              name="lastName"
              value={student.lastName}
              onChange={handleChange}
            />
          </div>

          {/* Father's & Mother's Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              label="Father's Name"
              name="fatherName"
              value={student.fatherName}
              onChange={handleChange}
            />
            <InputField
              label="Mother's Name"
              name="motherName"
              value={student.motherName}
              onChange={handleChange}
            />
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              label="Phone Number"
              name="phoneNo"
              value={student.phoneNo}
              onChange={handleChange}
            />
            <InputField
              label="Alternate Phone"
              name="phoneNo2"
              value={student.phoneNo2}
              onChange={handleChange}
            />
          </div>
          <InputField
            label="Email"
            name="email"
            value={student.email}
            onChange={handleChange}
          />

          {/* Gender, DOB, Age */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-700 mb-1">Gender</label>
              <select
                name="gender"
                value={student.gender}
                onChange={handleChange}
                className="w-full border-gray-300 rounded-md p-2"
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <InputField
              label="Date of Birth"
              name="dob"
              type="date"
              value={student.dob?.slice(0, 10)}
              onChange={handleChange}
            />
            <InputField
              label="Age"
              name="age"
              type="number"
              value={student.age}
              onChange={handleChange}
            />
          </div>

          {/* Address */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              label="State"
              name="state"
              value={student.state}
              onChange={handleChange}
            />
            <InputField
              label="City"
              name="city"
              value={student.city}
              onChange={handleChange}
            />
          </div>
          <InputField
            label="Address"
            name="address"
            value={student.address}
            onChange={handleChange}
          />

          {/* Submit */}
          <button
            type="submit"
            disabled={uploading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow-md transition disabled:opacity-50"
          >
            {uploading ? "Uploading..." : "Update Profile"}
          </button>
        </form>
      </div>
    </div>
  );
};

// Reusable InputField Component
const InputField = ({ label, name, value, onChange, type = "text" }) => (
  <div>
    <label className="block text-gray-700 mb-1">{label}</label>
    <input
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      className="w-full border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  </div>
);

export default EditProfile;
